import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import {
  getGetDoclistQueryKey,
  useDeleteSectionPage,
  useRotateSectionPage,
} from "~/api/chat/chat";
import { GetDoclist200, PresignedUrls } from "~/api/model";
import { PageItem } from "~/features/chat/hooks/useDocList";
import logger from "~/utils/logger";
import { updateDocListSectionPage } from "../helpers/updateDocListSelectionPage";

type UsePageActionsProps = {
  organizationId: string;
  caseId: string;
  chatId: string;
  projectId?: string;
  selectedPage: PageItem | null;
  docList: GetDoclist200;
  clearSelectedPages: () => void;
  updatePageUrls: (pageId: string, urls: Partial<PresignedUrls>) => void;
};

export const usePageActions = ({
  organizationId,
  caseId,
  chatId,
  projectId,
  selectedPage,
  docList,
  clearSelectedPages,
  updatePageUrls,
}: UsePageActionsProps) => {
  const { mutateAsync: rotateSectionPage } = useRotateSectionPage();
  const { mutateAsync: deleteSectionPage } = useDeleteSectionPage();
  const queryClient = useQueryClient();

  const [isRotateLoading, setIsRotateLoading] = useState(false);

  const calcNewRotate = (currentRotate: number, rotate: number) => {
    const newRotate = currentRotate + rotate;
    if (newRotate >= 360) return 0;
    return newRotate;
  };

  const rotatePage = useCallback(async () => {
    if (!selectedPage) {
      return;
    }

    setIsRotateLoading(true);

    const { sectionId, pageId, rotate: pageRotate } = selectedPage;
    const newRotate = calcNewRotate(pageRotate ?? 0, 90);

    // Manually update the query data to reflect the new rotation
    // This avoids a refetch and provides a smoother UX
    queryClient.setQueryData(
      getGetDoclistQueryKey(organizationId, caseId),
      updateDocListSectionPage(sectionId, pageId, {
        rotate: newRotate,
      })
    );

    // Clear URLs when rotation starts to prevent showing stale images
    updatePageUrls(pageId, {
      highResUrl: null,
      retinaUrl: null,
      scanUrl: null,
      previewUrl: null,
    });

    try {
      const response = await rotateSectionPage({
        organizationId,
        projectId: projectId || caseId,
        sectionId,
        pageId,
        data: {
          rotate: newRotate,
        },
      });

      if (response.result[pageId]) {
        const { regular, retina, highRes } = response.result[pageId];
        updatePageUrls(pageId, {
          scanUrl: regular,
          highResUrl: highRes,
          retinaUrl: retina,
          previewUrl: null,
        });
      }

      // Invalidate the page URLs cache to force refetch of new rotated image URLs
      queryClient.invalidateQueries({
        queryKey: ["pageUrlsRotate", pageId],
      });
    } catch (error) {
      logger.error("Failed to rotate page:", error);
      // If the mutation fails, invalidate the query to refetch and correct the state
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
    } finally {
      setIsRotateLoading(false);
    }
  }, [
    selectedPage,
    rotateSectionPage,
    organizationId,
    projectId,
    caseId,
    queryClient,
    updatePageUrls,
  ]);

  const deletePage = useCallback(async () => {
    if (!docList || !selectedPage) return;

    try {
      await deleteSectionPage({
        organizationId,
        projectId: caseId,
        chatId,
        sectionId: selectedPage.sectionId,
        pageId: selectedPage.pageId,
        data: {
          updatedAt: docList.updatedAt,
        },
      });

      clearSelectedPages();

      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, caseId),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          // Handle outdated doclist - this should be handled by the parent component
          throw error;
        }
      }
      logger.error("Failed to delete pages:", error);
    }
  }, [
    docList,
    selectedPage,
    deleteSectionPage,
    organizationId,
    caseId,
    chatId,
    clearSelectedPages,
    queryClient,
  ]);

  return {
    rotatePage,
    deletePage,
    isRotating: isRotateLoading,
  };
};
